import { _decorator, Sprite, v4, SpriteFrame, director, Director, Label } from 'cc';
import SmoothMotionTrail from '../Effect/SmoothMotionTrail';
import ShaderManipulator from './ShaderManipulator';

const { ccclass, executeInEditMode } = _decorator;

@ccclass('UVLRTBProvider')
@executeInEditMode
export default class UVLRTBProvider extends ShaderManipulator {
    private _spriteFrameChangeCallback: (value: SpriteFrame) => SpriteFrame = null;
    private _textContentChangeCallback: (value: string) => string = null;
    private _lrtbDirty = false;

    protected get target() {
        return super.target as Sprite | Label;
    }

    private get targetSpriteFrame(): SpriteFrame {
        if (this.target?.spriteFrame instanceof SpriteFrame) {
            return this.target.spriteFrame;
        }
        return null;
    }

    protected findTarget() {
        const target = super.findTarget();
        if (!(target instanceof Sprite) && !(target instanceof Label) && !(target instanceof SmoothMotionTrail)) {
            throw new Error(`Unsupported UIRenderer component: ${target.constructor.name}`);
        }
        return target;
    }

    protected registerTargetEvent() {
        super.registerTargetEvent();
        // 動態合圖在第一次渲染後才會修改UV資訊，所以在第一次渲染後要更新一次
        director.once(Director.EVENT_AFTER_DRAW, this.updateLRTBProperty, this);
        if (this.target instanceof Label) {
            director.on(Director.EVENT_AFTER_DRAW, this.tryUpdateLRTBProperty, this);
        }
        if (this.target instanceof Sprite) {
            if (!this._spriteFrameChangeCallback) {
                this._spriteFrameChangeCallback = (spriteFrame) => {
                    if (this.target.spriteFrame) {
                        this.target.spriteFrame.off(SpriteFrame.EVENT_UV_UPDATED, this.updateLRTBProperty, this);
                    }
                    if (spriteFrame) {
                        this.updateLRTBProperty(spriteFrame);
                        // listen on spriteFrame uv change event
                        spriteFrame.on(SpriteFrame.EVENT_UV_UPDATED, this.updateLRTBProperty, this);
                    }
                    return spriteFrame;
                };
            }
            this.target.registerSpriteFrameChangeCallback(this._spriteFrameChangeCallback);
        }
        if (this.target instanceof Label) {
            if (!this._textContentChangeCallback) {
                this._textContentChangeCallback = (content) => {
                    this._lrtbDirty = true;
                    return content;
                };
            }
            this.target.registerTextChangeCallback(this._textContentChangeCallback);
        }
    }

    protected unregisterTargetEvent() {
        super.unregisterTargetEvent();
        if (this.target instanceof Label) {
            director.off(Director.EVENT_AFTER_DRAW, this.tryUpdateLRTBProperty, this);
        }
        if (this.target instanceof Label) {
            this.target.unregisterTextChangeCallback(this._textContentChangeCallback);
            this._textContentChangeCallback = null;
        }
        if (this.target instanceof Sprite) {
            if (this.target.spriteFrame) {
                this.target.spriteFrame.off(SpriteFrame.EVENT_UV_UPDATED, this.updateLRTBProperty, this);
            }
            this.target.unregisterSpriteFrameChangeCallback(this._spriteFrameChangeCallback);
            this._spriteFrameChangeCallback = null;
        }
    }

    protected syncProperty() {
        this.updateLRTBProperty();
    }

    protected tryUpdateLRTBProperty() {
        if (this._lrtbDirty) {
            this.updateLRTBProperty();
            this._lrtbDirty = false;
        }
    }

    protected updateLRTBProperty(spriteFrame: SpriteFrame = this.targetSpriteFrame) {
        if (!spriteFrame) {
            return;
        }
        const pass = this.targetPass;
        if (!pass) {
            return;
        }
        const { uv } = spriteFrame;
        pass.setUniform(pass.getHandle('uvLRTB'), v4(uv[0], uv[6], uv[5], uv[3]));
        pass.setUniform(pass.getHandle('spriteProp'), v4(+spriteFrame.rotated, 0, 0, 0));
    }
}
